import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { PaperPlaneTilt, Plus, Eye, PencilSimple, Trash, Users, CheckCircle, XCircle, Clock, Image as ImageIcon, VideoCamera, FileText, MapPin } from '@phosphor-icons/react';
import { Loader2 } from 'lucide-react';
import {
  listPublications, createPublication, deletePublication, getPublication,
  uploadPublicationMedia, publicationMediaUrl,
  acceptPublicationRecipient, declinePublicationRecipient,
  getDossiers, getDocuments,
  listSpaces, previewPublication,
} from '../services/api';
import { TERRITOIRES } from '../constants/countries';
import { COLLEGES } from '../constants/colleges';
import { AIAssistButton } from '../components/AIAssistButton';
import { PublicationAudienceMode } from '../components/publications/PublicationAudienceMode';
import { PublicationRecipientSelector } from '../components/publications/PublicationRecipientSelector';
import { EditPublicationDialog } from '../components/publications/EditPublicationDialog';
import { BulkPublicationDialog } from '../components/publications/BulkPublicationDialog';
import { formatApiError } from '../utils/apiError';

const RESOURCE_TYPES = [
  { value: 'mission', label: 'Mission coopérative', icon: FileText },
  { value: 'dossier', label: 'Dossier ESS', icon: FileText },
  { value: 'document', label: 'Document GED', icon: FileText },
  { value: 'photo', label: 'Photo', icon: ImageIcon },
  { value: 'video', label: 'Vidéo', icon: VideoCamera },
  { value: 'convention', label: 'Convention CMC', icon: FileText },
  { value: 'agenda_event', label: 'Événement / Agenda', icon: Clock },
];

const STATUS_COLORS = {
  received: 'bg-blue-100 text-blue-800',
  read: 'bg-purple-100 text-purple-800',
  reserved: 'bg-amber-100 text-amber-800',
  accepted: 'bg-emerald-100 text-emerald-800',
  declined: 'bg-red-100 text-red-800',
  expired: 'bg-gray-200 text-gray-700',
};

const STATUS_LABELS = { received: 'Reçu', read: 'Lu', reserved: 'Réservé', accepted: 'Accepté', declined: 'Refusé', expired: 'Expiré' };

function fmtDateTime(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' }); } catch { return iso; }
}

function localDateTimeInput(date) {
  return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
}

export default function PublicationsPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openCreate, setOpenCreate] = useState(false);
  const [openBulk, setOpenBulk] = useState(false);
  const [detailPub, setDetailPub] = useState(null);
  const [editingPub, setEditingPub] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await listPublications({ limit: 200 });
      setItems(r.data.items || []);
    } catch { toast.error('Erreur chargement publications'); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette publication ?')) return;
    await deletePublication(id);
    toast.success('Publication supprimée');
    load();
  };

  return (
    <div className="min-w-0 space-y-6 p-2 sm:p-6" data-testid="publications-page">
      <div className="flex min-w-0 flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <h1 className="text-2xl font-semibold text-[#1A2421] flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
            <PaperPlaneTilt size={22} className="text-[#2D5A4C]" /> Publications multi-espaces
          </h1>
          <p className="text-sm text-[#5E6D66]">Partager missions, dossiers, documents, photos, vidéos, conventions, agenda vers une audience combinée (territoire + collège + organisation).</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => setOpenBulk(true)} data-testid="bulk-publish-btn">Publier en masse</Button>
          <Button onClick={() => setOpenCreate(true)} className="bg-[#2D5A4C] hover:bg-[#1E4136]" data-testid="new-publication-btn">
            <Plus size={16} className="mr-1" /> Nouvelle publication
          </Button>
        </div>
      </div>

      {openBulk && <BulkPublicationDialog onClose={() => setOpenBulk(false)} onDone={() => { setOpenBulk(false); load(); }} onChanged={load} />}

      {loading && <Loader2 className="animate-spin" />}
      {!loading && items.length === 0 && (
        <Card className="border-[#E8E6E1]"><CardContent className="p-8 text-center text-[#5E6D66]">
          Aucune publication. Cliquez sur « Nouvelle publication ».
        </CardContent></Card>
      )}

      <div className="grid gap-3">
        {items.map((p) => {
          const rt = RESOURCE_TYPES.find((x) => x.value === p.resource_type);
          const Icon = rt?.icon || FileText;
          return (
            <Card key={p.id} className="border-[#E8E6E1]" data-testid={`publication-card-${p.id}`}>
              <CardContent className="min-w-0 p-4 flex flex-col sm:flex-row items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#2D5A4C]/10 flex items-center justify-center shrink-0">
                  <Icon size={20} className="text-[#2D5A4C]" />
                </div>
                <div className="w-full sm:w-auto flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="max-w-full break-words [overflow-wrap:anywhere] font-semibold text-[#1A2421]">{p.title}</h3>
                    <Badge variant="outline" className="text-[10px]">{rt?.label || p.resource_type}</Badge>
                    {p.is_expired && <Badge className="bg-gray-200 text-gray-700 text-[10px]">Expirée</Badge>}
                    {p.capacity != null && <Badge variant="outline" className="text-[10px] border-amber-300 text-amber-800">Capacité {p.capacity}</Badge>}
                  </div>
                  <p className="text-xs text-[#5E6D66] mt-1 line-clamp-2">{p.description || p.resource_snapshot?.title || '—'}</p>
                  <div className="flex gap-1.5 mt-2 flex-wrap text-[10px]">
                    {p.audience?.mode === 'all_portal_members' && <Badge variant="outline" data-testid={`pub-all-audience-${p.id}`}>Tous les membres du portail</Badge>}
                    {p.audience?.mode === 'individual' && <Badge variant="outline" data-testid={`pub-individual-audience-${p.id}`}>Destinataires sélectionnés</Badge>}
                    {(p.audience?.territoires || []).map((t) => <Badge key={t} variant="outline"><MapPin size={10} className="mr-0.5" />{t}</Badge>)}
                    {(p.audience?.colleges || []).map((c) => <Badge key={c} variant="outline">Collège {c}</Badge>)}
                    {(p.target_roles || []).map((r) => <Badge key={r} className="bg-[#2D5A4C]/10 text-[#2D5A4C]">{r}</Badge>)}
                  </div>
                  <div className="flex gap-2 mt-2 text-[10px] text-[#5E6D66]">
                    <span>Du {fmtDateTime(p.display_from)} au {fmtDateTime(p.display_until)}</span>
                  </div>
                  <div className="flex gap-1.5 mt-2 flex-wrap">
                    <Badge className="bg-blue-100 text-blue-800 text-[10px]" data-testid={`pub-stats-received-${p.id}`}>Reçu {p.stats?.received || 0}</Badge>
                    <Badge className="bg-purple-100 text-purple-800 text-[10px]">Lu {p.stats?.read || 0}</Badge>
                    <Badge className="bg-amber-100 text-amber-800 text-[10px]">Réservé {p.stats?.reserved || 0}</Badge>
                    <Badge className="bg-emerald-100 text-emerald-800 text-[10px]">Accepté {p.stats?.accepted || 0}</Badge>
                    <Badge className="bg-red-100 text-red-800 text-[10px]">Refusé {p.stats?.declined || 0}</Badge>
                    <Badge className="bg-gray-100 text-gray-700 text-[10px]">Total {p.stats?.total || 0}</Badge>
                  </div>
                </div>
                <div className="flex shrink-0 self-end sm:self-auto gap-1">
                  <Button variant="ghost" size="sm" title="Voir la publication" aria-label="Voir la publication" onClick={async () => { const r = await getPublication(p.id); setDetailPub(r.data); }} data-testid={`view-publication-${p.id}`}>
                    <Eye size={14} />
                  </Button>
                  <Button type="button" variant="ghost" size="sm" onClick={() => setEditingPub(p)} title="Modifier la publication" aria-label="Modifier la publication" data-testid={`edit-publication-${p.id}`}><PencilSimple size={14} /></Button>
                  <Button variant="ghost" size="sm" onClick={() => handleDelete(p.id)} data-testid={`delete-publication-${p.id}`}>
                    <Trash size={14} className="text-red-600" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {openCreate && <CreatePublicationDialog onClose={() => setOpenCreate(false)} onCreated={() => { setOpenCreate(false); load(); }} />}
      {editingPub && <EditPublicationDialog publication={editingPub} onClose={() => setEditingPub(null)} onSaved={() => { setEditingPub(null); load(); }} />}
      {detailPub && <PublicationDetailDialog pub={detailPub} onClose={() => setDetailPub(null)} onReload={async () => { const r = await getPublication(detailPub.id); setDetailPub(r.data); load(); }} />}
    </div>
  );
}

function CreatePublicationDialog({ onClose, onCreated }) {
  const [step, setStep] = useState(1);
  const [resourceType, setResourceType] = useState('mission');
  const [resourceId, setResourceId] = useState('');
  const [resourceOptions, setResourceOptions] = useState([]);
  const [mediaFile, setMediaFile] = useState(null);
  const [uploaded, setUploaded] = useState(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [audienceMode, setAudienceMode] = useState('targeted');
  const [selectedRecipients, setSelectedRecipients] = useState([]);
  const [territoires, setTerritoires] = useState([]);
  const [colleges, setColleges] = useState([]);
  const [targetRoles, setTargetRoles] = useState(['membre_externe']);
  const [displayFrom, setDisplayFrom] = useState(() => localDateTimeInput(new Date()));
  const [displayUntil, setDisplayUntil] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() + 30); return localDateTimeInput(d);
  });
  const [capacity, setCapacity] = useState('');
  const [notifyEmail, setNotifyEmail] = useState(false);
  const [saving, setSaving] = useState(false);
  const [spaces, setSpaces] = useState([]);
  const [spaceId, setSpaceId] = useState('');
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState('');
  const previewVersion = useRef(0);
  const submitPending = useRef(false);
  const submissionVersion = useRef(0);
  const publishStarted = useRef(false);
  const [publishing, setPublishing] = useState(false);

  const goToStep = next => {
    if (publishStarted.current) return;
    submissionVersion.current += 1;
    submitPending.current = false; setSaving(false);
    setStep(next);
  };
  const closeDialog = () => {
    if (publishStarted.current) return;
    submissionVersion.current += 1;
    onClose();
  };

  const payload = useMemo(() => {
    const targeted = audienceMode === 'targeted';
    const iso = value => value && !Number.isNaN(Date.parse(value)) ? new Date(value).toISOString() : undefined;
    return {
      resource_type: resourceType, resource_id: resourceId || undefined,
      media: uploaded ? { object_path: uploaded.object_path, mime_type: uploaded.mime_type, size: uploaded.size, filename: uploaded.filename } : undefined,
      title: title || undefined, description,
      audience: { mode: audienceMode, territoires: targeted ? territoires : [], colleges: targeted ? colleges : [], user_ids: audienceMode === 'individual' ? selectedRecipients.map(item => item.user_id) : [] },
      target_roles: targeted ? targetRoles : ['membre_externe', 'partenaire'],
      space_id: targeted && spaceId ? spaceId : undefined,
      display_from: iso(displayFrom), display_until: iso(displayUntil),
      capacity: capacity ? Number(capacity) : null, notify_email: notifyEmail,
    };
  }, [audienceMode, selectedRecipients, resourceType, resourceId, uploaded, title, description, territoires, colleges, targetRoles, spaceId, displayFrom, displayUntil, capacity, notifyEmail]);

  useEffect(() => {
    listSpaces().then((r) => setSpaces(r.data.items || [])).catch(() => {});
  }, []);

  useEffect(() => {
    (async () => {
      try {
        if (resourceType === 'mission') {
          const r = await getDossiers({ type_dossier: 'mission_coop_cipaesse' });
          const r2 = await getDossiers({ type_dossier: 'mission_coop_cipascop' });
          setResourceOptions([...(r.data.items || []), ...(r2.data.items || [])].map((d) => ({ id: d.id, label: `${d.numero_dossier} — ${d.mission_intitule || d.objet_besoin || ''}` })));
        } else if (resourceType === 'dossier') {
          const r = await getDossiers({ limit: 200 });
          setResourceOptions((r.data.items || []).map((d) => ({ id: d.id, label: `${d.numero_dossier} — ${d.objet_besoin || ''}` })));
        } else if (resourceType === 'document' || resourceType === 'photo' || resourceType === 'video') {
          const r = await getDocuments({});
          setResourceOptions((r.data.items || r.data || []).map((d) => ({ id: d.id, label: `${d.titre || d.filename || d.id}` })));
        } else {
          setResourceOptions([]);
        }
      } catch { setResourceOptions([]); }
    })();
  }, [resourceType]);

  const toggle = (arr, setter, v) => setter(arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);

  const handleUpload = async () => {
    if (!mediaFile) return;
    try {
      const r = await uploadPublicationMedia(mediaFile, resourceType === 'video' ? 'video' : 'photo');
      setUploaded(r.data);
      toast.success('Média téléversé');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur upload'); }
  };

  const handleSubmit = async () => {
    if (submitPending.current) return;
    if (!payload.display_from || !payload.display_until || payload.display_until <= payload.display_from) return toast.error('La fin d’affichage doit être postérieure au début.');
    if (payload.capacity !== null && (!Number.isInteger(payload.capacity) || payload.capacity < 1)) return toast.error('La capacité doit être un nombre entier supérieur à zéro.');
    submitPending.current = true;
    const version = ++submissionVersion.current;
    setSaving(true);
    try {
      // Recontrôle sans envoi juste avant de publier ; aucun élargissement implicite.
      const check = await previewPublication(payload);
      if (version !== submissionVersion.current) return;
      setPreviewData(check.data);
      if (!check.data.recipients_count) return;
      publishStarted.current = true; setPublishing(true);
      await createPublication(payload);
      toast.success('Publication créée');
      onCreated();
    } catch (e) {
      if (version === submissionVersion.current) {
        const message = publishStarted.current && !e.response ? 'Enregistrement non confirmé. Vérifiez la liste avant une nouvelle tentative pour éviter un doublon.' : formatApiError(e, 'Erreur création');
        setPreviewError(message); toast.error(message);
      }
    }
    finally { if (version === submissionVersion.current) { submitPending.current = false; publishStarted.current = false; setSaving(false); setPublishing(false); } }
  };

  const handlePreview = useCallback(async () => {
    const version = ++previewVersion.current;
    setPreviewLoading(true);
    setPreviewData(null);
    setPreviewError('');
    try {
      const r = await previewPublication(payload);
      if (version === previewVersion.current) setPreviewData(r.data);
    } catch (e) { if (version === previewVersion.current) setPreviewError(formatApiError(e, 'Impossible de vérifier l’audience.')); }
    finally { if (version === previewVersion.current) setPreviewLoading(false); }
  }, [payload]);

  useEffect(() => {
    if (step === 3) handlePreview();
    else { setPreviewData(null); setPreviewLoading(false); setPreviewError(''); }
    return () => { previewVersion.current += 1; };
  }, [step, handlePreview]);

  return (
    <Dialog open onOpenChange={closeDialog}>
      <DialogContent className="min-w-0 grid-cols-[minmax(0,1fr)] max-w-2xl max-h-[90vh] overflow-y-auto" showCloseButton={!publishing} data-testid="new-publication-dialog">
        <DialogHeader><DialogTitle>Nouvelle publication (étape {step}/3)</DialogTitle><DialogDescription className="sr-only" data-testid="pub-dialog-description">Ressource, destinataires et période d’affichage de l’annonce.</DialogDescription></DialogHeader>
        <nav aria-label="Étapes de la publication" className="flex min-w-0 flex-wrap gap-2 border-b border-[#E8E6E1] pb-3" data-testid="pub-step-navigation">
          {['Ressource', 'Destinataires', 'Publication'].map((label, index) => <Button key={label} type="button" size="sm" variant={step === index + 1 ? 'default' : 'outline'} disabled={publishing || index + 1 > step} aria-current={step === index + 1 ? 'step' : undefined} onClick={() => goToStep(index + 1)} data-testid={`pub-go-step-${index + 1}`}>{index + 1}. {label}</Button>)}
        </nav>

        {step === 1 && (
          <div className="min-w-0 space-y-3">
            <div>
              <Label className="text-xs">Type de ressource</Label>
              <Select value={resourceType} onValueChange={value => { setResourceType(value); setResourceId(''); setUploaded(null); setMediaFile(null); }}>
                <SelectTrigger data-testid="pub-resource-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {RESOURCE_TYPES.map((r) => <SelectItem key={r.value} value={r.value} data-testid={`pub-resource-type-${r.value}`}>{r.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {resourceOptions.length > 0 && (
              <div>
                <Label className="text-xs">Sélectionner dans le CRM</Label>
                <Select value={resourceId} onValueChange={setResourceId}>
                  <SelectTrigger className="min-w-0 [&>span]:truncate" data-testid="pub-resource-id"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    {resourceOptions.slice(0, 100).map((o) => <SelectItem key={o.id} value={o.id} data-testid={`pub-resource-option-${o.id}`}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            {(resourceType === 'photo' || resourceType === 'video') && (
              <div className="border border-dashed border-[#E8E6E1] rounded p-3">
                <Label className="text-xs">Ou téléverser un nouveau média</Label>
                <div className="flex gap-2 items-center mt-1">
                  <input type="file" accept={resourceType === 'photo' ? 'image/*' : 'video/*'} onChange={(e) => setMediaFile(e.target.files?.[0])} data-testid="pub-media-file" />
                  <Button size="sm" variant="outline" onClick={handleUpload} disabled={!mediaFile} data-testid="pub-media-upload">Téléverser</Button>
                </div>
                {uploaded && <p className="text-[10px] text-emerald-700 mt-1">✓ {uploaded.filename} téléversé</p>}
              </div>
            )}
            <div>
              <Label className="text-xs">Titre (facultatif — repris de la ressource sinon)</Label>
              <Input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="pub-title" />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label className="text-xs">Description</Label>
                <AIAssistButton
                  value={description}
                  field="Description d'une publication / mission partagée"
                  onApply={(t) => setDescription(t)}
                  testId="ai-assist-pub-desc"
                />
              </div>
              <Textarea rows={3} value={description} onChange={(e) => setDescription(e.target.value)} data-testid="pub-description" />
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={closeDialog} data-testid="pub-cancel">Annuler</Button>
              <Button type="button" onClick={() => goToStep(2)} disabled={!resourceId && !uploaded?.object_path} className="bg-[#2D5A4C] hover:bg-[#1E4136]" data-testid="pub-step-next-1">Suivant</Button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-3">
            <PublicationAudienceMode value={audienceMode} onChange={setAudienceMode} />
            {audienceMode === 'individual' && <PublicationRecipientSelector value={selectedRecipients} onChange={setSelectedRecipients} />}
            <fieldset disabled={audienceMode !== 'targeted'} hidden={audienceMode === 'individual'} className={`min-w-0 space-y-3 ${audienceMode !== 'targeted' ? 'opacity-45' : ''}`} aria-disabled={audienceMode !== 'targeted'} data-testid="pub-targeted-fields">
            {spaces.length > 0 && (
              <div>
                <Label className="text-xs">Espace pré-enregistré (facultatif)</Label>
                <Select value={spaceId || 'none'} onValueChange={value => setSpaceId(value === 'none' ? '' : value)} disabled={audienceMode !== 'targeted'}>
                  <SelectTrigger data-testid="pub-space-select"><SelectValue placeholder="— Aucun —" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none" data-testid="pub-space-none">— Aucun —</SelectItem>
                    {spaces.map((s) => <SelectItem key={s.id} value={s.id} data-testid={`pub-space-${s.id}`}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <p className="text-[10px] text-[#5E6D66] mt-1">L'espace fusionne son audience avec vos choix ci-dessous.</p>
              </div>
            )}
            <div>
              <Label className="text-xs">Territoires</Label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 max-h-40 overflow-y-auto border border-[#E8E6E1] rounded p-2">
                {TERRITOIRES.map((t) => (
                  <label key={t.value} className="flex items-center gap-2 text-xs">
                    <input type="checkbox" checked={territoires.includes(t.value)} onChange={() => toggle(territoires, setTerritoires, t.value)} data-testid={`pub-territoire-${t.iso}`} />
                    {t.label}
                  </label>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-xs">Collèges coopératifs</Label>
              <div className="flex flex-wrap gap-2">
                {COLLEGES.map((c) => (
                  <label key={c.value} className="flex items-center gap-1 text-xs px-2 py-1 border border-[#E8E6E1] rounded">
                    <input type="checkbox" checked={colleges.includes(c.value)} onChange={() => toggle(colleges, setColleges, c.value)} data-testid={`pub-college-${c.value}`} />
                    {c.value} — {c.label}
                  </label>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-xs">Rôles cibles</Label>
              <div className="flex gap-2">
                {['membre_externe', 'partenaire'].map((r) => (
                  <label key={r} className="flex items-center gap-1 text-xs">
                    <input type="checkbox" checked={targetRoles.includes(r)} onChange={() => toggle(targetRoles, setTargetRoles, r)} data-testid={`pub-role-${r}`} />
                    {r === 'membre_externe' ? 'Membres du portail' : 'Partenaires'}
                  </label>
                ))}
              </div>
            </div>
            </fieldset>
            <div className="flex justify-between">
              <Button type="button" variant="outline" onClick={() => goToStep(1)} data-testid="pub-back-resource">Retour</Button>
              <Button type="button" onClick={() => goToStep(3)} disabled={audienceMode === 'individual' && selectedRecipients.length === 0} className="bg-[#2D5A4C] hover:bg-[#1E4136]" data-testid="pub-step-next-2">Suivant</Button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Affichage à partir de</Label>
                <Input type="datetime-local" disabled={saving} value={displayFrom} onChange={(e) => setDisplayFrom(e.target.value)} data-testid="pub-display-from" />
              </div>
              <div>
                <Label className="text-xs">Affichage jusqu'à</Label>
                <Input type="datetime-local" disabled={saving} value={displayUntil} onChange={(e) => setDisplayUntil(e.target.value)} data-testid="pub-display-until" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Capacité (nombre de places, laisser vide = illimité)</Label>
              <Input type="number" min="1" disabled={saving} value={capacity} onChange={(e) => setCapacity(e.target.value)} data-testid="pub-capacity" />
            </div>
            <label className="flex items-center gap-2 text-xs">
              <input type="checkbox" disabled={saving} checked={notifyEmail} onChange={(e) => setNotifyEmail(e.target.checked)} data-testid="pub-notify-email" />
              Notifier les destinataires par email (Brevo)
            </label>
            {previewLoading && <p role="status" className="text-xs text-[#5E6D66]" data-testid="pub-audience-checking">Vérification des destinataires…</p>}
            {previewError && <p role="alert" className="break-words border-l-4 border-red-500 bg-red-50 p-3 text-sm text-red-800" data-testid="pub-preview-error">{previewError}</p>}
            {previewData?.audience_warning && <p role="alert" className="break-words border-l-4 border-amber-500 bg-amber-50 p-3 text-sm text-amber-800" data-testid="pub-audience-warning">{previewData.audience_warning}</p>}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => goToStep(2)} disabled={publishing} data-testid="pub-back-audience">Retour</Button>
              <Button variant="outline" onClick={handlePreview} disabled={previewLoading || saving} data-testid="pub-preview-btn">
                {previewLoading && <Loader2 className="animate-spin mr-2" size={14} />} Aperçu portail
              </Button>
              <Button onClick={handleSubmit} disabled={saving || previewLoading || !previewData?.recipients_count || Boolean(previewError)} className="bg-[#2D5A4C] hover:bg-[#1E4136]" data-testid="pub-submit">
                {saving && <Loader2 className="animate-spin mr-2" size={14} />} Publier
              </Button>
            </DialogFooter>
            {previewData && (
              <div className="mt-3 border-t border-[#E8E6E1] pt-3" data-testid="pub-preview-panel">
                <p className="text-xs font-semibold text-[#1A2421] mb-2" data-testid="pub-preview-count">Aperçu portail — {previewData.recipients_count} destinataire(s) résolu(s)</p>
                {previewData.audience_mode === 'all_portal_members' && <p className="mb-2 text-xs font-medium text-[#2D5A4C]" data-testid="pub-preview-all-members">Tous les membres du portail</p>}
                {previewData.audience_mode === 'individual' && <p className="mb-2 text-xs font-medium text-[#2D5A4C]" data-testid="pub-preview-individual">Destinataires sélectionnés</p>}
                <div className="border border-[#E8E6E1] rounded p-3 bg-[#F9F8F5]">
                  <p className="font-semibold text-sm">{previewData.portal_card.title}</p>
                  <p className="text-xs text-[#5E6D66]">{previewData.portal_card.description || '—'}</p>
                  <p className="text-[10px] mt-1">Type : {previewData.portal_card.resource_type} · Capacité : {previewData.portal_card.capacity ?? '∞'}</p>
                </div>
                {previewData.recipients_preview.length > 0 && (
                  <div className="mt-2 max-h-32 overflow-y-auto text-[11px]">
                    {previewData.recipients_preview.slice(0, 10).map((r, i) => (
                      <div key={r.user_id || i} className="break-all py-0.5" data-testid={`pub-preview-recipient-${r.user_id || i}`}>• {r.name} · {r.email}</div>
                    ))}
                    {previewData.recipients_preview.length > 10 && <p className="text-[#5E6D66]">…et {previewData.recipients_preview.length - 10} autres</p>}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function PublicationDetailDialog({ pub, onClose, onReload }) {
  const grouped = useMemo(() => {
    const g = { received: [], read: [], reserved: [], accepted: [], declined: [], expired: [] };
    (pub.recipients || []).forEach((r) => { (g[r.status] || (g[r.status] = [])).push(r); });
    return g;
  }, [pub]);

  const handleAccept = async (rid) => {
    try { await acceptPublicationRecipient(pub.id, rid); toast.success('Réservation acceptée'); onReload(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
  };
  const handleDecline = async (rid) => {
    try { await declinePublicationRecipient(pub.id, rid); toast.success('Refus enregistré'); onReload(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="min-w-0 grid-cols-[minmax(0,1fr)] max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="publication-detail-dialog">
        <DialogHeader><DialogTitle className="break-words [overflow-wrap:anywhere]">{pub.title}</DialogTitle><DialogDescription className="sr-only" data-testid="pub-detail-description">Audience et réponses des destinataires de cette publication.</DialogDescription></DialogHeader>
        <p className="text-xs text-[#5E6D66]">{pub.description}</p>
        {pub.media?.object_path && (
          pub.resource_type === 'photo'
            ? <img src={publicationMediaUrl(pub.media.object_path)} alt="" className="rounded max-h-64" />
            : <video src={publicationMediaUrl(pub.media.object_path)} controls className="rounded max-h-64" />
        )}
        <div className="text-xs" data-testid="pub-detail-audience">
          <span className="font-semibold">Audience : </span>
          {pub.audience?.mode === 'all_portal_members' ? 'Tous les membres du portail' : pub.audience?.mode === 'individual' ? 'Destinataires sélectionnés' : [...(pub.audience?.territoires || []), ...(pub.audience?.colleges || []).map((c) => `Collège ${c}`)].join(', ') || '—'}
        </div>
        {['reserved', 'received', 'read', 'accepted', 'declined', 'expired'].map((s) => (
          (grouped[s] || []).length > 0 && (
            <div key={s} className="border-t border-[#E8E6E1] pt-2">
              <div className="text-xs font-semibold mb-1 flex items-center gap-1"><Badge className={STATUS_COLORS[s]}>{STATUS_LABELS[s]}</Badge> <span>({grouped[s].length})</span></div>
              <div className="space-y-1">
                {grouped[s].map((r) => (
                  <div key={r.id} className="flex items-center gap-2 p-1.5 rounded border border-[#E8E6E1] text-xs" data-testid={`recipient-row-${r.id}`}>
                    <Users size={12} />
                    <span className="flex-1 truncate">{r.name} · {r.email}</span>
                    {s === 'reserved' && (
                      <>
                        <Button size="sm" variant="ghost" onClick={() => handleAccept(r.id)} data-testid={`accept-recipient-${r.id}`}>
                          <CheckCircle size={14} className="text-emerald-600" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => handleDecline(r.id)} data-testid={`decline-recipient-${r.id}`}>
                          <XCircle size={14} className="text-red-600" />
                        </Button>
                      </>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )
        ))}
      </DialogContent>
    </Dialog>
  );
}


